// Content for the public program pages (/programs/:slug), the header Programs
// menu and the footer Programs column. Adding a program here adds its page and
// its links; nothing else needs to change.

export const programs = [
  {
    slug: "simulation",
    name: "Simulation",
    heading: "ChartFlow for",
    headingAccent: "Simulation",
    tagline:
      "Unfolding scenarios, time control, and quick reset between groups put your SimOps team in the driver's seat.",
    metaDescription:
      "ChartFlow is an academic EHR built for simulation labs: real-time scenario control, time control, quick reset, and 100+ pre-built patient scenarios.",
    builtFor: ["Simulation Centers", "Interprofessional Education"],
    image: {
      src: "/assets/homePage/chartflow-simulation.png",
      alt: "ChartFlow simulation interface showing real-time patient monitoring and control panel",
    },
    overview: {
      heading: "The Most Powerful EHR for Simulation",
      body: "ChartFlow gives simulation staff live control of the patient chart while students document in a realistic EHR.",
      bullets: [
        "Pause, fast-forward, and quickly reset patient charts between groups.",
        "Drop in new orders, labs, vital signs, and more using our Events feature.",
        "Build your own patients or copy & edit one of our 100+ pre-built scenarios.",
        "Sign-in made easy with support for ID Cards, Barcodes, and Single Sign-On (SSO) via SAML.",
      ],
    },
    highlights: [
      {
        title: "Badge Sign-In",
        body: "Assign a computer, laptop, or tablet to a simulation activity. Students scan their ID badge and go straight to it.",
      },
      {
        title: "Real-Time Control",
        body: "Pause or fast-forward time, release events, and message students with Simulation Chat, all while the scenario runs.",
      },
      {
        title: "Reset & Debrief",
        body: "Reset charts for the next group in one click. Past runs stay saved to review and grade during debriefing.",
      },
    ],
    workflow: {
      heading: "How a simulation day works",
      steps: [
        {
          title: "Choose a patient",
          body: "Start from one of our 100+ pre-built patient scenarios, or build your own.",
        },
        {
          title: "Set up the room",
          body: "Assign a computer, laptop, or tablet to the simulation activity so students go straight to it when they badge in.",
        },
        {
          title: "Run the scenario",
          body: "Pause and fast-forward time, and drop in new orders, labs, and vital signs as the scenario unfolds.",
        },
        {
          title: "Reset for the next group",
          body: "Reset the chart and the next group starts fresh.",
        },
      ],
    },
  },
  {
    slug: "nursing",
    name: "Nursing",
    heading: "ChartFlow for",
    headingAccent: "Nursing",
    tagline: "Prepare nursing students at all levels for real-world documentation.",
    metaDescription:
      "ChartFlow is an academic EHR for nursing programs: a realistic eMAR with barcode scanning, 50+ flowsheets, 100+ patient scenarios, and real-time simulation.",
    builtFor: ["LPN", "ADN", "BSN", "MEPN", "MSN", "DNP"],
    image: {
      src: "/assets/homePage/chartflow-nursing-mar.png",
      alt: "ChartFlow nursing eMAR (electronic Medication Administration Record) interface",
    },
    overview: {
      heading: "Documentation practice from the first semester to the last",
      body: "Use the same EHR in the classroom, the skills lab, simulation, and clinical.",
      bullets: [
        "Practice medication administration & barcode scanning using our realistic eMAR. Wallcur Practi-Meds & Pocket Nurse Demo Dose medications are supported.",
        "Withdraw medications from a simulated automated dispensing cabinet, with controlled substance counts, witness, and waste documentation.",
        "Reduce clinical paperwork and collect care plans with New Patient Activities.",
        "100+ Pre-built Patient Scenarios — Edit ours or build your own!",
        "Over 50 flowsheets & sections, including: Assessments, Vital Signs, Intake & Output, OB, Labs, Screenings, SBAR.",
      ],
    },
    highlights: [
      {
        title: "Classroom",
        body: "Bring a patient's health record into a lecture, homework assignment, or case study review.",
      },
      {
        title: "Simulation",
        body: "Change the scenario on the fly, pause & fast-forward time, and quickly reset between groups of students.",
      },
      {
        title: "Clinical",
        body: "Reduce the clinical paperwork you collect by allowing students to complete documentation as part of their assignment.",
      },
    ],
    workflow: {
      heading: "How nursing programs use ChartFlow",
      steps: [
        {
          title: "Assign a patient",
          body: "Pick a pre-built scenario or build your own, and add it to an activity in your course.",
        },
        {
          title: "Students chart",
          body: "Students review orders, document assessments and vital signs, and administer medications on the eMAR.",
        },
        {
          title: "Run it live",
          body: "In the sim lab, control time and drop in new orders, labs, and vital signs as the scenario unfolds.",
        },
        {
          title: "Review and grade",
          body: "Review each student's documentation and grade it in ChartFlow.",
        },
      ],
    },
  },
  {
    slug: "medical-assistant",
    name: "Medical Assistant",
    heading: "ChartFlow for",
    headingAccent: "Medical Assisting",
    tagline:
      "ChartFlow's Outpatient layout provides a realistic environment for students to practice reviewing & documenting on a patient chart.",
    metaDescription:
      "ChartFlow is an academic EHR for medical assistant programs: outpatient charting, scheduling, billing & coding, and two ready-to-use MA courses.",
    builtFor: ["Medical Assistant", "Dental Assistant"],
    image: {
      src: "/assets/homePage/chartflow-medical-assistant.png",
      alt: "ChartFlow medical assistant interface showing outpatient layout and appointment scheduling",
    },
    spotlight: {
      heading: "Practice Calendar",
      badge: "Coming soon",
      body: "A front-office calendar that drives the whole visit. Students schedule, check in, and chart the visit, then complete forms like prior authorizations and superbills, all from one appointment.",
      image: {
        src: "/assets/homePage/chartflow-practice-calendar.png",
        alt: "ChartFlow practice calendar in week view with scheduled appointments",
      },
    },
    overview: {
      heading: "Front office to exam room",
      body: "Students practice the administrative and clinical sides of an outpatient visit in one chart.",
      bullets: [
        "Practice Appointment Scheduling and Patient Registration.",
        "Billing & Coding section incorporates Diagnosis, Procedural, and Billing fields for student practice.",
        "Review physician notes, labs, past visits, schedule follow-ups, and more.",
        "Two ready-to-use medical assistant courses: 7 CCMA labs with lesson plans, plus 5 self-guided activities with learning modules.",
      ],
    },
    highlights: [
      {
        title: "Front Office",
        body: "Appointment Scheduling and Patient Registration give students practice with the work that starts every visit.",
      },
      {
        title: "Clinical",
        body: "Students review physician notes, labs, and past visits, then document on the patient chart.",
      },
      {
        title: "Billing & Coding",
        body: "Diagnosis, Procedural, and Billing fields let students practice coding a visit.",
      },
    ],
    workflow: {
      heading: "How medical assistant programs use ChartFlow",
      steps: [
        {
          title: "Start with a ready-made course",
          body: "7 CCMA labs with lesson plans, plus 5 self-guided activities with learning modules.",
        },
        {
          title: "Students work the visit",
          body: "Register the patient, review the chart, and document the visit in the Outpatient layout.",
        },
        {
          title: "Code and bill",
          body: "Students complete the Diagnosis, Procedural, and Billing fields for the visit.",
        },
        {
          title: "Review and grade",
          body: "Review each student's documentation and grade it in ChartFlow.",
        },
      ],
    },
  },
  {
    slug: "allied-health",
    name: "Allied Health",
    heading: "ChartFlow for",
    headingAccent: "Allied Health",
    tagline:
      "Realistic documentation practice for every allied health discipline, in the same chart the whole care team uses.",
    metaDescription:
      "ChartFlow is an academic EHR for allied health programs, with specialty documentation for Respiratory, PT, OT, Dietetics, Phlebotomy, and EMS.",
    builtFor: [
      "Respiratory Therapy",
      "Physical Therapy",
      "Physical Therapy Assistant",
      "Occupational Therapy",
      "Occupational Therapy Assistant",
      "Dietetics",
      "Phlebotomy",
      "EMS",
    ],
    image: {
      src: "/assets/homePage/chartflow-allied-health-cte.png",
      alt: "ChartFlow allied health and CTE interface showing specialty documentation features",
    },
    overview: {
      heading: "One chart, every discipline",
      body: "Each program documents in the same patient record, the way care teams do in practice.",
      bullets: [
        "Four visit layouts: Inpatient, Long-Term Care, Outpatient, and EMS Run Report.",
        "Specialty documentation for Respiratory, Physical Therapy, Occupational Therapy, and Dietetics programs.",
        "Venipuncture, Specimen Collection, and other interventions for phlebotomy and lab programs.",
        "20+ lab panels, including ABG, plus 40+ screenings.",
      ],
    },
    highlights: [
      {
        title: "Discipline-Specific Charting",
        body: "Students practice in the documentation their discipline uses, not a generic nursing chart.",
      },
      {
        title: "Every Care Setting",
        body: "Practice in the setting your students are preparing for, from the hospital floor to the ambulance.",
      },
      {
        title: "Interprofessional Education",
        body: "Students from different programs document on the same patient, just like a real care team.",
      },
    ],
    workflow: {
      heading: "How allied health programs use ChartFlow",
      steps: [
        {
          title: "Assign a patient",
          body: "Pick a pre-built scenario or build your own, and add it to an activity in your course.",
        },
        {
          title: "Students review the chart",
          body: "Students read orders, notes, and results before they see the patient.",
        },
        {
          title: "Students document",
          body: "Students chart their assessment and treatment in the documentation for their discipline.",
        },
        {
          title: "Review and grade",
          body: "Review each student's documentation and grade it in ChartFlow.",
        },
      ],
    },
  },
  {
    slug: "career-technical-education",
    name: "Career & Technical Education",
    heading: "ChartFlow for",
    headingAccent: "Career & Technical Education",
    tagline: "Hands-on EHR practice for every health science pathway, from medical assisting to CNA, with ready-made courses you can teach on day one.",
    metaDescription:
      "ChartFlow is an academic EHR for every high school health science pathway, from medical assisting to CNA, with ready-made courses and Chromebook support.",
    builtFor: ["Medical Assisting", "CNA", "Health Science", "Patient Care Technician", "EMS", "Medication Aide"],
    image: {
      src: "/assets/homePage/chartflow-allied-health-cte.png",
      alt: "ChartFlow allied health and CTE interface showing specialty documentation features",
    },
    overview: {
      heading: "One EHR for every pathway",
      body: "Every CTE program is different. ChartFlow supports each pathway your students explore, in one realistic EHR.",
      bullets: [
        "Medical assisting: outpatient visits, Appointment Scheduling, Patient Registration, and Billing & Coding.",
        "CNA and PCT: Activities of Daily Living (ADL), Interventions, Screenings, and vital signs.",
        "Ready-made courses with lesson plans, including CCMA, Medication Aide, and Student Orientation.",
        "100+ pre-built patient scenarios. Edit ours or build your own.",
      ],
    },
    highlights: [
      {
        title: "Every Pathway",
        body: "Medical assisting, CNA, EMS, and general health science, all in one platform.",
      },
      {
        title: "Front Office to Billing",
        body: "Students schedule, chart, and code a full outpatient visit.",
      },
      {
        title: "Chromebook-Ready",
        body: "Runs in the browser on Chromebooks, laptops, and tablets. Nothing for IT to install.",
      },
    ],
    workflow: {
      heading: "How CTE programs use ChartFlow",
      steps: [
        {
          title: "Start with a ready-made course",
          body: "Pick a pre-built course with lesson plans for your pathway, or assign one of 100+ patient scenarios.",
        },
        {
          title: "Students chart",
          body: "Students document the work of their pathway, from billing and coding to ADLs and vital signs.",
        },
        {
          title: "Review and grade",
          body: "Review each student's documentation and grade it in ChartFlow.",
        },
      ],
    },
  },
];

export function getProgram(slug) {
  return programs.find((program) => program.slug === slug);
}
